import { useQueryClient } from '@zg-rentals/react-query5';
import type { InfiniteData } from '@zg-rentals/react-query5';
import type { SatelliteWebLandlordGetLatestConversationsForInboxResponseDto } from '@zg-rentals/ts-satellite-client';
import queryString from 'query-string';
import { useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import { InboxPageMobileView } from '../types/InboxPageMobileView';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import useDevices from './useDevices';
import {
  inboxPageConversationIdToFilterByStore,
  inboxPageConversationSearchTextStore,
  inboxPageShowEmptyContainerStore,
} from '../lib/inboxSearchListStores';
import { buildRequestParams, getInboxConversationsQueryKey } from './useInboxConversationsQuery';

type ChangeConversationArgs = {
  conversationId: string;
  listingAlias: string;
  newMobileView?: InboxPageMobileView;
  replacePath?: boolean;
};

export type ChangeFiltersArgs = {
  folder?: SatelliteConversationFolderTypeV2;
  renterProgressStatus?: SatelliteConversationRenterProgressStatusTypeV2;
  byProperty?: boolean;
  readRepliedStatus?: SatelliteConversationReadRepliedStatusTypeV2;
  listingAlias?: string;
  daysBack?: number;
  mobileView?: InboxPageMobileView;
};

export function buildConversationSlug(
  listingAlias: string | null | undefined,
  conversationId: string | null | undefined,
): Array<string> {
  if (conversationId) {
    return [listingAlias || '', conversationId];
  }
  if (listingAlias) {
    return [listingAlias];
  }
  return [];
}

export default () => {
  const queryClient = useQueryClient();
  const contextObj = useContext(InboxPageContext);
  const { isMobileView } = useDevices();
  const router = useRouter();
  const inboxPageConversationIdToFilterBy = inboxPageConversationIdToFilterByStore.useValue();

  const changeFiltersOrFolders = useCallback(
    async ({
      folder,
      renterProgressStatus,
      byProperty,
      readRepliedStatus,
      listingAlias,
    }: ChangeFiltersArgs): Promise<void> => {
      if (typeof byProperty === 'undefined') {
        byProperty = contextObj.isByProperty;
      }

      if (!listingAlias) {
        listingAlias = contextObj.listingAlias;
      }
      const newListingAlias = byProperty ? listingAlias : null;
      const filtersQueryString = getFiltersQueryString({
        daysBack: contextObj.daysBack,
        folder,
        renterProgressStatus,
        byProperty,
        readRepliedStatus,
      });

      const params = buildRequestParams({
        conversationId: inboxPageConversationIdToFilterBy || undefined,
        daysBack: contextObj.daysBack,
        folder,
        listingAlias: byProperty ? listingAlias : null,
        renterProgressStatus,
        readRepliedStatus,
        clientTimeZone: contextObj.clientTimeZone,
      });
      const queryKey = getInboxConversationsQueryKey(params);
      const cachedData =
        queryClient.getQueryData<InfiniteData<SatelliteWebLandlordGetLatestConversationsForInboxResponseDto>>(queryKey);
      const cachedConversations = cachedData?.pages.flatMap((page) => page.conversations) ?? [];

      if (cachedConversations.length) {
        const conversation = cachedConversations[0];

        updatePath({
          conversationId: conversation.conversationId,
          listingAlias: conversation.listingDetails?.listingAlias ?? '',
          byProperty,
          filtersQueryString,
        });
      } else {
        updatePath({
          conversationId: null,
          listingAlias: byProperty || contextObj.isByProperty ? newListingAlias : null,
          byProperty,
          filtersQueryString,
        });
      }
    },
    [contextObj],
  );

  const resetFiltersAndFolders = useCallback(() => {
    inboxPageShowEmptyContainerStore.set(false);
    inboxPageConversationSearchTextStore.set('');
    inboxPageConversationIdToFilterByStore.set('');

    const { conversationId, folder, listingAlias } = contextObj;
    const preserveConversationSlug =
      folder === SatelliteConversationFolderTypeV2.INBOX && conversationId && listingAlias;

    router.push({
      pathname: router.pathname,
      query: preserveConversationSlug ? { conversationSlug: [listingAlias, conversationId] } : {},
    });
  }, [contextObj]);

  const clearConversation = useCallback(() => {
    // Drop the selected conversation from the URL while keeping the current filters (readRepliedStatus,
    // folder, etc. live in router.query) and, in by-property mode, the property scope. Uses replace so
    // this correction doesn't leave a back-button entry pointing at the stale selection.
    const conversationSlug = buildConversationSlug(contextObj.isByProperty ? contextObj.listingAlias : null, null);

    router.replace({
      pathname: router.pathname,
      query: {
        ...router.query,
        conversationSlug,
      },
    });
  }, [contextObj.isByProperty, contextObj.listingAlias, router.query]);

  const changeConversation = useCallback(
    async ({
      conversationId,
      listingAlias,
      newMobileView,
      replacePath = false,
    }: ChangeConversationArgs): Promise<void> => {
      if (isMobileView) {
        const mobileView = newMobileView || InboxPageMobileView.ConversationList;
        router.query.mobileView = mobileView;
      }

      const conversationSlug = buildConversationSlug(listingAlias, conversationId);

      const updatedRouter = {
        pathname: router.pathname,
        query: {
          ...router.query,
          conversationSlug,
        },
      };

      if (replacePath) {
        router.replace(updatedRouter);
      } else {
        router.push(updatedRouter);
      }
    },
    [isMobileView, router.query],
  );

  const updatePath = useCallback(
    ({
      conversationId,
      listingAlias,
      byProperty,
      filtersQueryString,
    }: {
      conversationId?: string | null;
      listingAlias?: string | null;
      filtersQueryString?: string;
      byProperty?: boolean;
    }): void => {
      const parsedQuery = queryString.parse(filtersQueryString);

      if (!byProperty && parsedQuery.byProperty) {
        delete parsedQuery.byProperty;
      }

      const conversationSlug = buildConversationSlug(listingAlias, conversationId);

      router.push({
        pathname: router.pathname,
        query: {
          ...parsedQuery,
          conversationSlug,
        },
      });
    },
    [contextObj],
  );

  const getFiltersQueryString = useCallback(
    ({
      daysBack,
      folder,
      renterProgressStatus,
      byProperty,
      readRepliedStatus,
      mobileView,
    }: ChangeFiltersArgs): string => {
      const paramsObj: Record<string, string> = {};

      if (folder && folder != SatelliteConversationFolderTypeV2.INBOX) {
        if (folder === SatelliteConversationFolderTypeV2.SPAM) {
          paramsObj.folder = 'REPORTED';
        } else {
          paramsObj.folder = folder;
        }
      }

      if (readRepliedStatus && readRepliedStatus != SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL) {
        paramsObj.readRepliedStatus = readRepliedStatus;
      }

      if (renterProgressStatus && renterProgressStatus != SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL) {
        paramsObj.renterProgressStatus = renterProgressStatus;
      }

      if (byProperty) {
        paramsObj.byProperty = 'true';
      }

      if (mobileView || isMobileView) {
        paramsObj.mobileView = mobileView || InboxPageMobileView.ConversationList;
      }

      if (daysBack) {
        paramsObj.daysBack = daysBack.toString();
      }

      if (Object.keys(paramsObj).length === 0) {
        return '';
      }

      return `?${queryString.stringify(paramsObj)}`;
    },
    [],
  );

  const changeMobileView = useCallback(
    (nextView: InboxPageMobileView): void => {
      updatePath({
        byProperty: contextObj.isByProperty,
        conversationId: contextObj.conversationId,
        listingAlias: contextObj.listingAlias,
        filtersQueryString: getFiltersQueryString({
          daysBack: contextObj.daysBack,
          folder: contextObj.folder,
          readRepliedStatus: contextObj.readRepliedStatus,
          renterProgressStatus: contextObj.renterProgressStatus,
          byProperty: contextObj.isByProperty,
          mobileView: nextView,
        }),
      });
    },
    [contextObj],
  );

  const removeQueryParam = useCallback(
    (queryParamToDelete: string, shouldReplaceHistory = true) => {
      const updatedQuery = router.query;
      delete updatedQuery[queryParamToDelete];
      const updatedRouter = {
        pathname: router.pathname,
        query: updatedQuery,
      };

      return shouldReplaceHistory ? router.replace(updatedRouter) : router.push(updatedRouter);
    },
    [router.query],
  );

  return {
    changeMobileView,
    changeConversation,
    changeFiltersOrFolders,
    clearConversation,
    getFiltersQueryString,
    updatePath,
    removeQueryParam,
    resetFiltersAndFolders,
  };
};
